import { isGridApiReady, useCallbackRef, type CareOrder } from '@talos/kyoko';
import type { SelectionChangedEvent } from 'ag-grid-community';
import { useState } from 'react';
import type { SelectableCareOrder } from './SelectableCareOrder';

interface UseCareOrderNettingPreviewSelectionParams {
  /** Whether row actions should add compatible orders to the netting preview or remove member orders from it. */
  action: 'add' | 'remove';
  /** Called with each updated care order ID and the selection state to apply. */
  onSelectionChanged: (orderID: string, selected: boolean) => void;
}

/**
 * Tracks AG Grid row selection and exposes helpers for applying the same care order
 * selection state to either one row or the currently selected rows.
 */
export const useCareOrderNettingPreviewSelection = ({
  action,
  onSelectionChanged,
}: UseCareOrderNettingPreviewSelectionParams) => {
  const [selectedRows, setSelectedRows] = useState<SelectableCareOrder[]>([]);
  const selected = action === 'add';

  const updateCareOrderSelection = useCallbackRef((careOrder: CareOrder) => {
    onSelectionChanged(careOrder.OrderID, selected);
  });

  const updateSelectedCareOrdersSelection = useCallbackRef(() => {
    selectedRows.forEach(careOrder => {
      onSelectionChanged(careOrder.OrderID, selected);
    });
    setSelectedRows([]);
  });

  const handleSelectionChanged = useCallbackRef((event: SelectionChangedEvent<SelectableCareOrder>) => {
    if (!isGridApiReady(event.api)) {
      return;
    }

    setSelectedRows(event.api.getSelectedRows());
  });

  return {
    selectedRows,
    updateCareOrderSelection,
    updateSelectedCareOrdersSelection,
    handleSelectionChanged,
  };
};
